import { forwardRef, useState } from 'react';
import c from 'classnames';
import { FieldError, UseFormRegisterReturn } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';

import { Icon } from '../Icon/Icon';
import { IconName } from '../Icon/getSvgByName';
import Typography from '../Typography/Typography';
import styles from './THField.module.css';

export const PLACEHOLDER = {
  firstName: 'Jane',
  lastName: 'Appleby',
  email: 'jane@example.com',
  new_email: 'jane@example.com',
  phone: '(555) 555-5555',
  new_phone: '(555) 555-5555',
  password: '••••••••',
  passwordVisible: 'a nice, long password',
  verification_code: '123 456',
  impersonateId: 'e.g., aabfs5-678afg-98afdg-896afdg',
};

export interface FieldProps extends Omit<UseFormRegisterReturn, 'ref'> {
  name: string;
  label: string;
  labelClasses?: string;
  labelInside?: boolean;
  type: string;
  // Accept error objects from any react-hook-form schema without requiring
  // callers to specify generics on this UI component.
  error?: FieldError;
  hideErrors?: boolean;
  autoComplete?: string;
  placeholder?: string;
  icon?: IconName;
  center?: boolean;
  defaultValue?: string | number;
  description?: string;
  classes?: string;
  classNames?: string;
  step?: string;
  showRequiredIndicator?: boolean;
}

export function THFieldInner(
  props: FieldProps,
  ref: React.Ref<HTMLInputElement>,
) {
  const {
    label,
    labelClasses,
    labelInside = false,
    name,
    type = 'text',
    error,
    hideErrors,
    placeholder,
    icon,
    center = false,
    defaultValue,
    classes,
    classNames,
    description,
    showRequiredIndicator = false,
    required,
    ...rest
  } = props;
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const togglePasswordVisibility = () => {
    setIsPasswordVisible(!isPasswordVisible);
  };

  return (
    <div className={c(styles.field, classes, classNames)}>
      {!labelInside && (
        <label htmlFor={name} className={c(styles.label, labelClasses)}>
          <Typography variant='body-s'>
            {label}
            {showRequiredIndicator && (
              <span
                className={c(
                  styles.requiredIndicator,
                  required && styles.required,
                )}
              >
                {required ? '*' : ' (optional)'}
              </span>
            )}
          </Typography>
          {type === 'password' && (
            <div onClick={togglePasswordVisibility}>
              <Typography variant='body-s' className='clickable'>
                {isPasswordVisible ? 'Hide' : 'Show '}
              </Typography>
            </div>
          )}
        </label>
      )}
      {!!description && (
        <Typography variant='label' className={styles.description}>
          {description}
        </Typography>
      )}
      <div className={styles.inputContainer}>
        <input
          type={type === 'password' && isPasswordVisible ? 'text' : type}
          id={name}
          name={name}
          placeholder={
            placeholder
              ? placeholder
              : type === 'password'
                ? isPasswordVisible
                  ? PLACEHOLDER.passwordVisible
                  : PLACEHOLDER.password
                : PLACEHOLDER[name as keyof typeof PLACEHOLDER]
          }
          aria-label={label}
          className={c(
            styles.input,
            !!error && styles.hasError,
            !!icon && styles.hasIcon,
            labelInside && styles.labelInside,
            {
              'text-center': center,
            },
          )}
          defaultValue={defaultValue}
          {...rest} // { onChange, onBlur }
          ref={ref}
        />
        {labelInside && (
          <label
            htmlFor={name}
            className={c(styles.label, labelClasses, styles.inputInset)}
          >
            <Typography variant='label'>
              {label}
              {showRequiredIndicator && required && (
                <span className={styles.requiredIndicator}>*</span>
              )}
            </Typography>
            {type === 'password' && (
              <div onClick={togglePasswordVisibility}>
                <Typography variant='label' className='clickable'>
                  {isPasswordVisible ? 'Hide' : 'Show'}
                </Typography>
              </div>
            )}
          </label>
        )}
        {icon && (
          <div className={c(styles.inputInset, styles.icon)}>
            <Icon name={icon} size='s' />
          </div>
        )}
      </div>
      {!hideErrors && (
        <div className={styles.errors}>
          {!!error && (
            <ErrorMessage
              errors={{ [name]: error }}
              name={name}
              render={({ message }: { message: string }) => (
                <div
                  className={styles.error}
                  id={`error-${name}`}
                  key={message}
                  role='alert'
                >
                  <Icon name='alertCircle' size='xs' /> {message}
                </div>
              )}
            />
          )}
        </div>
      )}
    </div>
  );
}

export const THField = forwardRef(THFieldInner);
export default THField;
